import React, { useState, useEffect, useRef } from 'react';
import usePersistedState from '../utils/useListPreferences';
import { useNavigate, Link } from 'react-router-dom';
import { Play, Search, ChevronLeft, ChevronRight, Sparkles, RotateCw, Bot, CheckCircle, History, Lock } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import EditEssayDialog from './EditEssayDialog';
import Navbar from './Navbar';
import TestModeDialog from './TestModeDialog';
import AIFeedbackDialog from './AiFeedbackDialog';
import { create } from 'framer-motion/m';
import { checkExamAccess } from '../utils/examAccess';
import secureStorage from '../utils/secureStorage';
import { API_BASE } from '../config/api';
import DifficultyBadge from './DifficultyBadge';
import ForecastStars, { ForecastLegend } from './ForecastStars';

const Writing_Fe = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [liveCounts, setLiveCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Remembered so returning from a test lands back on the page it was on.
  const [currentPage, setCurrentPage] = usePersistedState('listPage:writing', 1);
  const [hideDone, setHideDone] = usePersistedState('hideDone:writing', false, 'local');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [username, setUsername] = useState('');
  // Writing VIP only — the local fallback AI-grading quota shown before /ai/writing/quota
  // answers. Listening/Reading (incl. the legacy "all_skills" = L&R bundle) do NOT count.
  const [hasAnyVipForAi, setHasAnyVipForAi] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const dropdownRef = useRef(null);
  const testsPerPage = 6;
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [modeForTest, setModeForTest] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [evaluatedTasks, setEvaluatedTasks] = useState({});
  const [aiRemaining, setAiRemaining] = useState(0);
  const [aiQuota, setAiQuota] = useState(null); // authoritative server quota for Writing AI grading
  const [histOpen, setHistOpen] = useState(null);   // test_id whose history dropdown is open
  const [histData, setHistData] = useState({});     // test_id -> [{part_number, band}]
  const [histLoading, setHistLoading] = useState({});


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    const currentUser = localStorage.getItem('username');
    if (currentUser) {
      setUsername(currentUser);
    }

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const [testsResponse, subscriptionResponse] = await Promise.all([
          fetch(`${API_BASE}/student/writing/tasks`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_BASE}/customer/vip/subscription/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (testsResponse.status === 401 || subscriptionResponse.status === 401) {
          navigate('/login');
          return;
        }

        if (testsResponse.ok && subscriptionResponse.ok) {
          const [testsData, subscriptionData] = await Promise.all([
            testsResponse.json(),
            subscriptionResponse.json()
          ]);

          setAccountStatus(subscriptionData);

          setHasAnyVipForAi(!!subscriptionData.has_writing_access);
          const mapped = testsData.map(exam => ({
            id: exam.exam_id,
            title: exam.title,
            created_at: exam.created_at,
            test_id: exam.test_id,
            parts: exam.parts,
            difficultyAvg: exam.difficulty_avg,
            occurrenceSum: exam.occurrence_sum || 0,
            is_completed: exam.is_completed,
            overall_band: exam.overall_band,
            vip_locked: exam.vip_locked
          }));
          setTests(mapped);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Live "người đang làm bài" per writing test (scope = test_id), refreshed periodically.
  useEffect(() => {
    if (!tests.length) return;
    const token = localStorage.getItem('token');
    const scopes = tests.map(t => t.test_id).join(',');
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const res = await fetch(`${API_BASE}/student/live-presence?exam_ids=${scopes}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setLiveCounts(data.counts || {});
        }
      } catch (e) { /* best-effort */ }
    };
    fetchCounts();
    const iv = setInterval(fetchCounts, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [tests]);

  // Deep-link from the public SEO landing pages: /writing_list?open=<exam_id>
  // opens that full test once the access-filtered list has loaded. No match
  // (no access / VIP-only) => normal list shows, gating untouched.
  const autoOpenHandledRef = useRef(false);
  useEffect(() => {
    if (loading || autoOpenHandledRef.current || !tests.length) return;
    const openId = parseInt(new URLSearchParams(window.location.search).get('open'), 10);
    if (!openId) return;
    const target = tests.find(t => t.id === openId);
    if (target && target.parts && target.parts.length) {
      autoOpenHandledRef.current = true;
      navigate('/writing_test_room', {
        state: {
          taskId: target.parts[0].task_id,
          testId: target.test_id,
          testTitle: target.title
        }
      });
    }
  }, [tests, loading, navigate]);

  useEffect(() => {
    const role = localStorage.getItem('role');
    const usernameKey = localStorage.getItem('username') || 'unknown';
    const now = new Date();
    const nowUtcMs = now.getTime() + (now.getTimezoneOffset() * 60000);
    const vnMs = nowUtcMs + (7 * 60 * 60 * 1000);
    const vn = new Date(vnMs);
    const dateKey = `${vn.getUTCFullYear()}-${String(vn.getUTCMonth() + 1).padStart(2, '0')}-${String(vn.getUTCDate()).padStart(2, '0')}`;
    const storeKey = `aiEvalCounters:${usernameKey}`;
    const counters = JSON.parse(localStorage.getItem(storeKey) || '{}');
    const isVipOrStudent = hasAnyVipForAi || role === 'student';
    // Unified daily AI-scoring count (shared by full test + forecast): non-VIP = 2/day.
    const limit = isVipOrStudent ? 6 : 2;
    const used = counters[dateKey]?.writing || 0;
    setAiRemaining(Math.max(0, limit - used));
  }, [hasAnyVipForAi, aiDialogOpen, aiLoading]);

  // Authoritative Writing-AI grade quota from the server (VIP-aware; NOT affected by
  // the Writing Assistant chat, which has its own separate limit).
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/ai/writing/quota`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (r.ok) setAiQuota(await r.json());
      } catch (e) { /* ignore */ }
    })();
  }, [aiDialogOpen, aiLoading]);

  const handleAIFeedback = async (task) => {
    if (!task.is_completed) {
      return;
    }

    const role = localStorage.getItem('role');
    const usernameKey = localStorage.getItem('username') || 'unknown';
    const now = new Date();
    const nowUtcMs = now.getTime() + (now.getTimezoneOffset() * 60000);
    const vnMs = nowUtcMs + (7 * 60 * 60 * 1000);
    const vn = new Date(vnMs);
    const dateKey = `${vn.getUTCFullYear()}-${String(vn.getUTCMonth() + 1).padStart(2, '0')}-${String(vn.getUTCDate()).padStart(2, '0')}`;
    const storeKey = `aiEvalCounters:${usernameKey}`;
    const counters = JSON.parse(localStorage.getItem(storeKey) || '{}');
    if (!counters[dateKey]) counters[dateKey] = { full: 0, forecast: 0, total: 0 };
    const isVipOrStudent = hasAnyVipForAi || role === 'student';
    if (isVipOrStudent) {
      if (counters[dateKey].total >= 6) {
        setAiResult({ error: 'Bạn đã vượt quá giới hạn đánh giá AI trong ngày (6).' });
        setAiDialogOpen(true);
        return;
      }
    } else {
      if (counters[dateKey].full >= 1) {
        setAiResult({ error: 'Tài khoản thường chỉ được đánh giá 1 bài full mỗi ngày.' });
        setAiDialogOpen(true);
        return;
      }
    }

    if (aiRemaining <= 0) {
      setAiResult({ error: 'Bạn đã hết số lượt đánh giá AI trong ngày.' });
      setAiDialogOpen(true);
      return;
    }

    setAiLoading(true);
    setAiDialogOpen(true);

    try {
      const token = localStorage.getItem('token');

      // Fetch essay data
      const essayResponse = await fetch(`${API_BASE}/student/writing/part/${task.task_id}/essay`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!essayResponse.ok) {
        throw new Error('Failed to fetch essay data');
      }

      const essayData = await essayResponse.json();

      if (!essayData.essay?.answer_text) {
        setAiResult({ error: 'No essay text found for evaluation. Please complete the test first.' });
        return;
      }

      // AI evaluation request with retry mechanism
      let retryCount = 0;
      const maxRetries = 2;

      while (retryCount <= maxRetries) {
        try {
          const response = await fetch(`${API_BASE}/ai/evaluate-and-save/${task.task_id}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              essay_text: essayData.essay.answer_text,
              instructions: essayData.instructions || ''
            })
          });

          let data;
          const responseText = await response.text();

          try {
            data = JSON.parse(responseText);
          } catch (parseError) {
            console.error('Failed to parse AI response:', responseText);
            throw new Error('Invalid response format from AI service');
          }

          if (!response.ok) {
            throw new Error(data.detail || 'AI service error occurred');
          }

          if (!data.evaluation_result) {
            throw new Error('Missing evaluation result in AI response');
          }

          setAiResult({
            task_id: data.task_id,
            evaluation_timestamp: data.evaluation_timestamp,
            band_score: data.evaluation_result.band_score,  // Changed from 'score' to 'band_score'
            word_count: data.word_count,
            answer_text: essayData.essay.answer_text,
            evaluation_result: data.evaluation_result
          });
          setEvaluatedTasks(prev => ({ ...prev, [task.task_id]: true }));
          counters[dateKey].writing = (counters[dateKey].writing || 0) + 1;
          localStorage.setItem(storeKey, JSON.stringify(counters));
          return;

        } catch (error) {
          console.error(`AI request attempt ${retryCount + 1} failed:`, error);
          if (retryCount === maxRetries) {
            throw new Error(`AI service failed after ${maxRetries + 1} attempts: ${error.message}`);
          }
          retryCount++;
          await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount))); // Exponential backoff
        }
      }

    } catch (error) {
      console.error('AI Feedback Error:', error);
      setAiResult({
        error: `Unable to process AI feedback: ${error.message}`
      });
    } finally {
      setAiLoading(false);
    }
  };
  const handleStartTest = async (test) => {
    if (test.is_completed) {
      setSelectedTest(test);
      setDialogOpen(true);
    } else {
      setModeForTest(test);   // pick Luyện tập / Thi thử first
    }
  };

  // Per-card history dropdown (like Reading/Listening): current version + past attempts.
  const openHist = async (testId) => {
    if (histOpen === testId) { setHistOpen(null); return; }
    setHistOpen(testId);
    if (histData[testId]) return;
    setHistLoading((p) => ({ ...p, [testId]: true }));
    try {
      const r = await fetch(`${API_BASE}/student/writing/test/${testId}/attempts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = r.ok ? await r.json() : [];
      setHistData((prev) => ({ ...prev, [testId]: data }));
    } catch (e) { setHistData((prev) => ({ ...prev, [testId]: [] })); }
    finally { setHistLoading((p) => ({ ...p, [testId]: false })); }
  };

  const startTestWithMode = (mode) => {
    const test = modeForTest;
    setModeForTest(null);
    if (test) navigate(`/writing_test_room`, {
      state: { taskId: test.parts[0].task_id, testId: test.test_id, testTitle: test.title, mode }
    });
  };

  const handleConfirmReset = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_BASE}/student/writing/test/${selectedTest.test_id}/reset`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const testsResponse = await fetch(`${API_BASE}/student/writing/tasks`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (testsResponse.ok) {
          const updatedTests = await testsResponse.json();
          setTests(updatedTests);
        }

        setDialogOpen(false);
        setModeForTest(selectedTest);   // let them pick Luyện tập / Thi thử for the retake
      }
    } catch (error) {
      console.error('Error resetting test:', error);
    } finally {
      setDialogOpen(false);
      setSelectedTest(null);
    }
  };

  const handleEditEssay = (task) => {
    setSelectedPart(task);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = (wasUpdated) => {
    if (wasUpdated) {
      const fetchTests = async () => {
        const token = localStorage.getItem('token');
        try {
          const response = await fetch(`${API_BASE}/student/writing/tasks`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (response.ok) {
            const data = await response.json();
            setTests(data);
          }
        } catch (error) {
          console.error('Error fetching tests:', error);
        }
      };
      fetchTests();
    }
    setEditDialogOpen(false);
    setSelectedPart(null);
  };

  const [sortOrder, setSortOrder] = useState('alphabet', 'latest', 'oldest');

  // "Ẩn bài đã làm" is a VIP filter, same as search and sort on this page.
  const canHideDone = accountStatus?.is_subscribed || localStorage.getItem('role') !== 'customer';

  const filteredTests = tests
    .filter(test => !(hideDone && canHideDone) || !test.is_completed)
    .filter(test => test.title.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      // Non-VIP không được sắp theo trúng tủ (khóa) — tránh lộ bài forecast qua thứ tự.
      const effSort = (sortOrder === 'forecast' && !aiQuota?.is_vip) ? 'alphabet' : sortOrder;
      switch (effSort) {
        case 'alphabet':
          // Split titles into text and number parts
          const [aText, aNum] = a.title.match(/([^\d]+)(\d+)/).slice(1);
          const [bText, bNum] = b.title.match(/([^\d]+)(\d+)/).slice(1);

          // Compare text parts first
          const textCompare = aText.localeCompare(bText);
          if (textCompare !== 0) return textCompare;

          // If text parts are same, compare numbers
          return parseInt(aNum) - parseInt(bNum);
        case 'latest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'difficulty': {
          // Easy first = higher avg band; tests not fully classified go last.
          const av = a.difficultyAvg, bv = b.difficultyAvg;
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          return bv - av;
        }
        case 'forecast':
          return (b.occurrenceSum || 0) - (a.occurrenceSum || 0);
        default:
          return a.title.localeCompare(b.title);
      }
    });
  // Add the select element in the search bar div
  <div className="flex flex-col md:flex-row gap-4 mb-8">
    <div className="flex-1 relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
      <input
        type="text"
        placeholder="Search tests..."
        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-lime-500 focus:border-lime-500"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
    </div>
    <select
      className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-lime-500 focus:border-lime-500"
      value={sortOrder}
      onChange={(e) => setSortOrder(e.target.value)}
    >
      <option value="alphabet">Theo Alphabet</option>
      <option value="forecast">Theo trúng tủ (cao trước)</option>
      <option value="difficulty">Theo độ khó (dễ trước)</option>
      <option value="latest">Mới nhất</option>
      <option value="oldest">Cũ nhất</option>
    </select>
  </div>

  const indexOfLastTest = currentPage * testsPerPage;
  const indexOfFirstTest = indexOfLastTest - testsPerPage;
  const currentTests = filteredTests.slice(indexOfFirstTest, indexOfLastTest);
  const totalPages = Math.ceil(filteredTests.length / testsPerPage);

  // The remembered page can point past the end once the list shrinks (search, or
  // hiding finished tests) — fall back to the last page that still has results.
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage, setCurrentPage]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center p-8 rounded-2xl bg-white shadow-lg">
          <div className="relative w-20 h-20 mb-6">
            {/* Spinning circles animation */}
            <div className="absolute inset-0 border-4 border-t-green-500 border-r-green-400 border-b-green-300 border-l-green-200 rounded-full animate-spin"></div>
            <div className="absolute inset-2 border-4 border-t-green-400 border-r-green-300 border-b-green-200 border-l-transparent rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
            <div className="absolute inset-4 border-4 border-t-green-300 border-r-green-200 border-b-transparent border-l-green-400 rounded-full animate-spin" style={{ animationDuration: '2s' }}></div>
          </div>

          <div className="text-xl font-medium text-gray-700 mb-2">Loading writing tests...</div>

          <div className="flex space-x-1.5 mt-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>

          <div className="mt-4 text-sm text-gray-500 max-w-xs text-center">
            Đang tải các bài kiểm tra IELTS Writing. Vui lòng đợi trong giây lát...
          </div>
        </div>
      </div>
    );
  }

  const renderTestCard = (test, index) => {
    // Full tests are open to everyone (spec: writing prompts are easy to find, so
    // open access is a draw). The forecast stars are hidden here on purpose so users
    // can't tell which tests are forecast picks — a different rule from the Forecast tab.
    return (
    <div
      key={test.test_id}
      className={`rounded-lg shadow hover:shadow-md transition-all duration-300 border p-2 relative ${test.is_completed ? 'bg-orange-50 border-[#eb7e37]' : 'bg-white border-gray-100'}`}
    >
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center min-w-0">
            <span className="text-[#0096b1] text-md italic mr-2">Test:</span>
            <span className="text-gray-700 truncate">{test.title}</span>
          </h3>
          {(test.is_completed || test.overall_band != null) && (
            <div className="relative shrink-0">
              {histOpen === test.test_id && <div className="fixed inset-0 z-40" onClick={() => setHistOpen(null)} />}
              <button onClick={() => openHist(test.test_id)} title="Lịch sử làm bài Writing"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200">
                <History className="w-4 h-4" /> Lịch sử
                <ChevronRight className={`w-3.5 h-3.5 transition-transform ${histOpen === test.test_id ? 'rotate-90' : ''}`} />
              </button>
              {histOpen === test.test_id && (() => {
                const attempts = histData[test.test_id] || [];
                const versions = [];
                if (test.overall_band != null) versions.push({ key: 'cur', label: 'Lượt hiện tại', overall: test.overall_band, current: true });
                attempts.forEach((a) => versions.push({ key: `a${a.attempt_number}`, label: `Lượt ${a.attempt_number}`, overall: a.overall_band, date: a.created_at, attempt_number: a.attempt_number }));
                return (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden text-left">
                  <div className="p-3 border-b border-gray-50 bg-gray-50/50">
                    <h4 className="text-sm font-semibold text-gray-700">Lịch sử bài thi</h4>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {histLoading[test.test_id] ? (
                      <div className="p-6 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0096b1] mx-auto" /></div>
                    ) : versions.length > 0 ? (
                      <>
                        {versions.slice(0, 2).map((v) => (
                          <button key={v.key}
                            onClick={() => { setHistOpen(null); navigate('/writing_review', { state: { testId: test.test_id, isForecast: false, attemptNumber: v.attempt_number } }); }}
                            className="w-full p-3 text-left hover:bg-[#0096b1]/5 border-b border-gray-50 transition-colors">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-semibold text-gray-700">{v.label}</span>
                              {v.overall != null
                                ? <span className="text-base font-bold text-[#0096b1] bg-[#0096b1]/10 px-2.5 py-1 rounded">Band {v.overall}</span>
                                : <span className="text-xs text-gray-400">Chưa chấm</span>}
                            </div>
                            {v.date && <div className="text-xs text-gray-400 mt-0.5">{new Date(v.date).toLocaleDateString('vi-VN')}</div>}
                          </button>
                        ))}
                        <button onClick={() => { setHistOpen(null); navigate('/exam-history'); }}
                          className="w-full text-center p-3 block text-sm font-semibold text-[#0096b1] hover:bg-[#0096b1]/5 transition-colors">
                          Xem tất cả lịch sử
                        </button>
                      </>
                    ) : (
                      <div className="p-4 text-center text-gray-500 text-sm">Chưa có kết quả chấm.</div>
                    )}
                  </div>
                </div>
                );
              })()}
            </div>
          )}
        </div>

        {liveCounts[test.test_id] > 0 && (
          <div className="mb-2 flex items-center gap-1.5 text-[#0096b1] font-semibold text-sm">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0096b1] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0096b1]"></span>
            </span>
            {liveCounts[test.test_id]} người đang làm bài
          </div>
        )}

        {test.overall_band != null && (
          <div className="mb-3 flex items-center justify-center gap-2 rounded-lg bg-[#0096b1]/5 border border-[#0096b1]/20 py-1.5">
            <span className="text-xs text-gray-500">Writing Overall:</span>
            <span className="text-lg font-extrabold text-[#0096b1]">{test.overall_band}</span>
          </div>
        )}
        <div className="space-y-2 mb-4">
          {test.parts.map((task) => (
            <div key={task.task_id} className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 py-1.5 px-2 rounded">
              <span className="flex items-center gap-2">
                Part {task.part_number}
                <span className="inline-flex items-center gap-2 flex-wrap">
                  <DifficultyBadge label={task.difficulty_label} />
                  {/* Non-VIP: hiện khóa ở MỌI đề (kể cả đề không có dự đoán) để không lộ bài nào là forecast.
                      VIP: thấy sao thật (chỉ có ở đề forecast). */}
                  {test.vip_locked
                    ? (
                        <button onClick={() => navigate('/vip-packages?type=writing')} title="Nâng cấp VIP Writing để xem dự đoán trúng tủ"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0096b1] hover:underline whitespace-nowrap">
                          <Lock className="w-3 h-3" /> Nâng cấp VIP để xem dự đoán
                        </button>
                      )
                    : <ForecastStars level={task.forecast_level} />}
                </span>
              </span>
              <div className="flex items-center gap-2">
                <span className="text-md">{task.word_limit} words</span>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => handleStartTest(test)}
          className={`w-full flex items-center justify-center gap-2 ${test.is_completed ? 'bg-[#eb7e37] hover:bg-[#d66e2a]' : 'bg-[#0096b1] hover:bg-[#007a90]'} text-white px-4 py-2 rounded-md transition-colors font-medium text-sm`}
        >
          {test.is_completed ? (
            <RotateCw className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4" />
          )}
          <span>{test.is_completed ? 'Làm Lại' : 'Làm Bài'}</span>
        </button>
      </div>
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li><Link to="/" className="text-gray-500 hover:text-[#0096b1]">Home</Link></li>
              <li><span className="text-gray-400 mx-2">/</span></li>
              <li><span className="text-[#0096b1] font-medium">Writing Tests</span></li>
            </ol>
          </nav>
          {aiQuota?.is_vip ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-[#0096b1]/25 bg-[#0096b1]/5 px-4 py-2 self-start sm:self-auto">
              <Sparkles className="w-4 h-4 text-[#0096b1] shrink-0" />
              <span className="text-sm text-gray-600">Chấm điểm AI</span>
              <span className="text-sm font-bold text-[#0096b1]">Không giới hạn</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 rounded-full border border-[#eb7e37]/30 bg-gradient-to-r from-[#0096b1]/5 to-[#eb7e37]/10 px-4 py-2 self-start sm:self-auto">
              <Sparkles className="w-4 h-4 text-[#eb7e37] shrink-0" />
              <span className="text-sm text-gray-600">Lượt chấm AI hôm nay</span>
              <span className="text-base font-extrabold text-[#eb7e37] tabular-nums">
                {aiQuota ? aiQuota.remaining : aiRemaining}<span className="text-gray-400 font-semibold text-sm">/{aiQuota ? aiQuota.limit : 2}</span>
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search tests..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-lime-500 focus:border-lime-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-lime-500 focus:border-lime-500"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="alphabet">Theo Alphabet</option>
      {/* "Theo trúng tủ" bị KHÓA cho non-VIP (để lại dấu vết): sắp theo trúng tủ sẽ lộ bài forecast. */}
      <option value="forecast" disabled={!aiQuota?.is_vip}>Theo trúng tủ (cao trước){!aiQuota?.is_vip ? ' — VIP' : ''}</option>
      <option value="difficulty">Theo độ khó (dễ trước)</option>
            <option value="latest">Mới nhất</option>
            <option value="oldest">Cũ nhất</option>
          </select>
          {canHideDone && (
          <label className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg cursor-pointer select-none bg-white hover:bg-gray-50 whitespace-nowrap">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[#0096b1] cursor-pointer"
              checked={hideDone}
              onChange={(e) => { setHideDone(e.target.checked); setCurrentPage(1); }}
            />
            <span className="text-sm font-medium text-gray-700">Ẩn bài đã làm</span>
          </label>
          )}
        </div>

        <div className="mb-4"><ForecastLegend /></div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentTests.map((test, index) => renderTestCard(test, index))}
        </div>

        {/* Add pagination controls */}
        <div className="flex justify-center items-center space-x-4 mt-8 mb-16">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={3} />
          </button>
          <span className="text-gray-600 font-bold">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            <ChevronRight className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        <TestModeDialog open={!!modeForTest} skill="writing" onSelect={startTestWithMode} onClose={() => setModeForTest(null)} />

        <ConfirmDialog
          isOpen={dialogOpen}
          message="Làm lại sẽ tạo một lượt thi mới. Kết quả lượt trước vẫn được lưu và bạn có thể xem lại trong Lịch sử. Bạn có muốn tiếp tục?"
          onConfirm={handleConfirmReset}
          onCancel={() => {
            setDialogOpen(false);
            setSelectedTest(null);
          }}
        />
        <EditEssayDialog
          isOpen={editDialogOpen}
          onClose={handleEditDialogClose}
          taskId={selectedPart}
          partNumber={selectedPart?.part_number}
        />
        <AIFeedbackDialog
          isOpen={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          result={aiResult}
          loading={aiLoading}
          setSelectedPart={setSelectedPart}
          setEditDialogOpen={setEditDialogOpen}
        />
      </div>
    </div>
  );
};

export default Writing_Fe;
